/* eslint-disable @typescript-eslint/naming-convention, no-underscore-dangle, id-denylist, id-match */

import { Component, Input, AfterViewInit, Output, EventEmitter, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { SchemaField, Document, DocumentSchema, SystemFieldType } from 'src/app/modules/core/store/models/document.model';
import { DocumentReader } from 'src/app/modules/core/store/models/document-reader';
import { DynamicField } from '../dynamic-field';
import { ViewComponent } from '../../../store/models/workflow.model';
import { Dataset } from 'src/app/modules/core/store/models/dataset.model';
import { Store } from '@ngrx/store';
import { catchError, debounceTime, map, switchMap, take, takeUntil } from 'rxjs/operators';
import { Subject, Observable, of } from 'rxjs';
import * as fromAuth from 'src/app/modules/authentication/store/auth';
import { UserApiService } from 'src/app/modules/management/store/users';
import { User } from 'src/app/modules/management/store/users/user.model';

@Component({
    selector: 'app-user-field',
    templateUrl: './user-field.component.html',
    styleUrls: ['./user-field.component.scss'],
    standalone: false
})
export class UserFieldComponent implements DynamicField, OnDestroy {
  @Output() valueChanged = new EventEmitter<DynamicField>();
  @Input() field: SchemaField;
  @Input() displayName: string;
  @Input() layerUId: string;
  @Input()
  set document(value: Document) {
    this.reader = new DocumentReader(value);
  }
  reader: DocumentReader;
  @Input() viewComponent: ViewComponent;
  isReadOnly: boolean;
  @Input() schema: DocumentSchema;
  @Input() dataset: Dataset;

  usersLoading: boolean;
  users: User[];
  usersInput$ = new Subject<string>();
  destroy$: Subject<void> = new Subject<void>();
  searchTerm: string;
  selectedUserId?: string;
  selectedUser?: User;

  set value(val: any) {
    if (val) {
      this.selectedUserId = val;
    } else {
      this.selectedUserId = null;
      this.selectedUser = null;
    }
  }
  get value(): any {
    return this.selectedUser?.uId;
  }

  showFieldAsEdit: boolean = false;
  private _isEdit: boolean = false;
  @Input() set isEdit(edit: boolean) {
    this._isEdit = edit;

    this.calculateShouldDisplayFieldForEdit();
  }

  constructor(private userApi: UserApiService, private store: Store<fromAuth.State>, private changeDetector: ChangeDetectorRef) {}

  ngOnDestroy(): void {
    this.valueChanged.complete();
  }

  calculateShouldDisplayFieldForEdit() {
    this.showFieldAsEdit = this._isEdit && !this.isReadOnly;
  }

  tpOnContentUpdated(): void {
    this.value = this.reader.getFieldValue(this.field?.systemName);

    if ((this.field.fieldType & SystemFieldType.Editable) !== SystemFieldType.Editable || this.viewComponent?.configuration?.isReadOnly) {
      this.isReadOnly = true;
    }
    if (this.selectedUserId) {
      this.getUser(this.selectedUserId);
    }

    this.usersInput$.pipe(debounceTime(400), takeUntil(this.destroy$)).subscribe(searchTerm => {
      if (this.searchTerm != searchTerm) {
        this.searchForUsers(searchTerm);
        this.searchTerm = searchTerm;
      } else {
        this.usersLoading = false;
      }
    });
  }

  getUser(userId: string): void {
    this.getUserById(userId)
      .pipe(take(1))
      .subscribe(users => {
        this.users = users;
        this.selectedUser = users?.length > 0 ? users[0] : null;
        this.usersLoading = false;
        this.changeDetector.detectChanges();
      });
  }

  searchForUsers(searchTerm: string): void {
    this.getUsers(searchTerm)
      .pipe(take(1))
      .subscribe(objects => {
        this.usersLoading = false;
        this.users = objects;
        this.changeDetector.detectChanges();
      });
  }

  getUsers(searchTerm: string = null): Observable<User[]> {
    this.usersLoading = true;

    return this.store.select(fromAuth.selectUserContext).pipe(
      take(1),
      switchMap(userContext => {
        return this.userApi
          .getUsers(userContext.organizationUId, {
            skip: 0,
            take: 20,
            sort: 'DisplayName',
            where: !!searchTerm
              ? `DisplayName.ToLower().Contains("${searchTerm.replace('"', '\\"').toLocaleLowerCase()}") AND IsServiceAccount==false`
              : 'IsServiceAccount==false'
          })
          .pipe(
            catchError(() => of({ data: [] })),
            map(({ data }) => {
              return data;
            })
          );
      })
    );
  }

  getUserById(userId: string): Observable<User[]> {
    this.usersLoading = true;

    return this.store.select(fromAuth.selectUserContext).pipe(
      take(1),
      switchMap(userContext => {
        return this.userApi
          .getUsers(userContext.organizationUId, {
            skip: 0,
            take: 1,
            where: `uid="${userId}"`
          })
          .pipe(
            catchError(() => of({ data: [] })),
            map(({ data }) => {
              return data;
            })
          );
      })
    );
  }

  onValueChanged(): void {
    this.valueChanged.emit(this);
  }
}
